import { getGameData } from '@/lib/aggregation';
import type { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import type { Region } from '@/types/constants';
import type { Game } from '@/types/games';
import type { GameViewModel } from '@/types/games/game-view-model';
import type { Metadata } from 'next';
import type { AbsoluteTemplateString } from 'next/dist/lib/metadata/types/metadata-types';
import type { LauncherUrlProps } from '@/utils/url/launcher-url';
import { toGameViewModel } from '../games/to-game-view-model';

export type GridLayoutCtx = {
    layout: PRISMIC_ZONE_LAYOUT;
    position: number;
};

export const getGame = async (gameName: string) => {
    const json = await getGameData(gameName);
    const content = json?.pageConfig?.content as Game;

    if (!content) return { data: null, uid: null };

    return content;
};

/**
 * @deprecated Prefer `adaptGame` at the data boundary.
 */
export const getGameProps = (
    gameProps: Game,
    region: Region,
    isMobile: boolean,
    gridLayoutCtx?: GridLayoutCtx,
    animatedTileEnabled = false
): GameViewModel => toGameViewModel(gameProps, { region, isMobile }, { gridLayoutCtx, animatedTileEnabled });

/** Serializable payload for launching a game from the same pipeline as lobby tiles (`getGame` → `adaptGame`). */
export type GameLaunchPayload = {
    launcherUrlProps: LauncherUrlProps;
    gameUid?: string;
    gameName?: string;
    gameId?: string;
    gameProvider?: string;
    ganId?: string | number | null;
};

/**
 * Resolves launcher props for a Prismic game UID using the same steps as lobby game tiles.
 * Build the href with `getLauncherUrl(payload.launcherUrlProps)` (server or client) so behavior matches `default-game`.
 */
export const resolveGameLaunchPayloadForUid = async (
    gameLinkUid: string,
    region: Region,
    isMobile: boolean
): Promise<GameLaunchPayload | null> => {
    const game = await getGame(gameLinkUid);

    if (!game?.uid) {
        return null;
    }

    const simplified = toGameViewModel(game as Game, { region, isMobile });

    return {
        launcherUrlProps: simplified.launcherUrlProps,
        gameUid: simplified.launcherUrlProps.gameUid,
        gameName: simplified.launcherUrlProps.gameName,
        gameId: simplified.launcherUrlProps.gameId,
        gameProvider: simplified.providerName,
        ganId: simplified.launcherUrlProps.ganId
    };
};

export const getGameMetadata = async (
    gameName: string,
    {
        defaultTitle,
        defaultDescription
    }: { defaultTitle: AbsoluteTemplateString | null; defaultDescription: string | null }
): Promise<Metadata> => {
    const data = (await getGame(gameName))?.data;

    return {
        title: data?.seo_meta_title ?? defaultTitle,
        description: data?.seo_meta_description ?? defaultDescription
    };
};
